import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { LIST_SERVER_PAGINATION_INITIAL_STATE, ListServerPaginationStateModel } from '../../../../common/models';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { ExportItem } from '../../../models';
import { ExportsListService } from './exports-list.service';
import { ExportsListDownloadFile, ExportsListInitialState, ExportsListLoad } from './exports-list.actions';
import { downloadBlob, getFileNameFromContentDispositionHeader } from '../../../../common/utils';
import { HttpResponse } from '@angular/common/http';
import { Error, CustomHttpErrorResponse } from '../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type ExportsListStateModel = ListServerPaginationStateModel<ExportItem>;

const initialState: ExportsListStateModel = LIST_SERVER_PAGINATION_INITIAL_STATE;

@State<ExportsListStateModel>({
  name: 'ExportsList',
  defaults: initialState,
})
@Injectable()
export class ExportsListState {
  constructor(private exportsListService: ExportsListService, private notificationService: NotificationService) {}

  @Selector()
  static getState(state: ExportsListStateModel): ExportsListStateModel {
    return state;
  }

  @Selector()
  static getItems(state: ExportsListStateModel): ExportItem[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: ExportsListStateModel): boolean {
    return state.loaded;
  }

  @Action(ExportsListLoad)
  getTransportManagementExportsList(
    ctx: StateContext<ExportsListStateModel>,
    { payload }: ExportsListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.exportsListService.getExportsList(payload).pipe(
      map((exportsList: ExportsListStateModel) => {
        ctx.patchState({ ...exportsList, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ExportsListDownloadFile)
  downloadExportsFile(
    ctx: StateContext<ExportsListStateModel>,
    { payload }: ExportsListDownloadFile,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.exportsListService.downloadExportsFile(payload).pipe(
      map((response: HttpResponse<Blob>) => {
        const fileName = getFileNameFromContentDispositionHeader(response);
        const { body } = response;
        downloadBlob(body, fileName);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ExportsListInitialState)
  setInitialState(ctx: StateContext<ExportsListInitialState>): void {
    ctx.setState(initialState);
  }
}
